import React, { useState } from 'react';
import { api } from './shared/utils/api';
import { Wave, Calendar, Books, ArrowRight, ArrowLeft, Upload, Pencil } from './Icons';

const PROVIDERS = ['BARBRI', 'BPP', 'Kaplan', 'Revise SQE', 'Epimeleia', 'ULaw', 'Other'];

// SQE1 exam cohorts — dates will be confirmed by SRA; update in Settings if different
const COHORTS = [
  { id: 'jul26-both',  label: 'July 2026',      sub: 'Both FLK1 & FLK2',          flk1: '2026-07-07', flk2: '2026-07-08' },
  { id: 'jan27-both',  label: 'January 2027',   sub: 'Both FLK1 & FLK2',          flk1: '2027-01-13', flk2: '2027-01-14' },
  { id: 'jul26-flk1',  label: 'Resitting FLK1', sub: 'FLK1 only, July 2026',      flk1: '2026-07-07', flk2: '' },
  { id: 'jul26-flk2',  label: 'Resitting FLK2', sub: 'FLK2 only, July 2026',      flk1: '',           flk2: '2026-07-08' },
  { id: 'manual',      label: 'Enter manually', sub: 'I know my specific dates',   flk1: null,         flk2: null },
];

export default function Onboarding({ userName, onComplete }) {
  const [step, setStep] = useState(1);
  const [name, setName]             = useState(userName || '');
  const [flk1Date, setFlk1Date]     = useState('');
  const [flk2Date, setFlk2Date]     = useState('');
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [showManualDates, setShowManualDates] = useState(false);
  const [provider, setProvider]     = useState('');
  const [providerOther, setProviderOther] = useState('');
  const [saving, setSaving]         = useState(false);
  const [mcqTarget, setMcqTarget] = useState('40');
  const [prepTarget, setPrepTarget] = useState('0.70');

  // Phase 2 — subject-area ratings
  const [subjectRatings, setSubjectRatings] = useState({});  // key: subject_area → float 0-1
  const [subjectAreas, setSubjectAreas] = useState([]);       // [{name, flk, weighting}]
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [savingSubjects, setSavingSubjects] = useState(false);

  // Save steps 1-4 settings then advance to step 5
  const saveAndAdvance = async (skipProvider) => {
    setSaving(true);
    const effectiveProvider = skipProvider ? '' : (provider === 'Other' ? providerOther : provider);
    const accountUpdate = { name };
    if (flk1Date) accountUpdate.exam_date_flk1 = flk1Date;
    if (flk2Date) accountUpdate.exam_date_flk2 = flk2Date;
    const settingsUpdate = {};
    if (flk1Date) settingsUpdate.flk1_exam_date = flk1Date;
    if (flk2Date) settingsUpdate.flk2_exam_date = flk2Date;
    if (effectiveProvider) settingsUpdate.material_provider = effectiveProvider;
    if (mcqTarget) settingsUpdate.mcq_daily_target = mcqTarget;
    settingsUpdate.target_confidence = prepTarget;
    await Promise.all([
      api('/api/account', { method: 'PATCH', body: JSON.stringify(accountUpdate) }),
      api('/api/settings', { method: 'POST', body: JSON.stringify(settingsUpdate) }),
    ]);
    setSaving(false);
    loadSubjectAreas();
    setStep(5);
  };

  // Complete onboarding — dest controls which tab the app opens on
  const finishOnboarding = async (dest = 'dashboard') => {
    await api('/api/settings', { method: 'POST', body: JSON.stringify({
      onboarding_complete: '1',
      tour_shown: '0',
    }) });
    onComplete(name, dest);
  };

  // Load subject areas for the L1 rating screen
  const loadSubjectAreas = async () => {
    setLoadingSubjects(true);
    try {
      const topics = await api('/api/topics');
      const seen = new Set();
      const areas = [];
      for (const t of topics) {
        const name = t.subject_area || t.main_topic || t.topic;
        if (name && !seen.has(name)) {
          seen.add(name);
          areas.push({
            name,
            flk: t.flk_number === 2 ? 'FLK2' : 'FLK1',
            weighting: t.exam_weighting || '',
          });
        }
      }
      setSubjectAreas(areas);
    } finally {
      setLoadingSubjects(false);
    }
  };

  const dots = (
    <div className="ob-dots">
      {[1, 2, 3, 4, 5, 6].map(n => (
        <span key={n} className={`ob-dot ${step === n ? 'active' : step > n ? 'done' : ''}`} />
      ))}
    </div>
  );

  // ── Step 1: Name ──
  if (step === 1) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon"><Wave size={32} /></div>
        <h2 className="ob-title">Welcome to SQETrack</h2>
        <p className="ob-text">
          One minute to set up. We will configure your exam dates, study materials, and get your dashboard working.
        </p>
        <label className="ob-label">What should we call you?</label>
        <input className="ob-input" value={name} onChange={e => setName(e.target.value)}
          placeholder="Your first name" autoFocus
          onKeyDown={e => e.key === 'Enter' && name.trim() && setStep(2)} />
        <button className="ob-btn" onClick={() => setStep(2)} disabled={!name.trim()}>
          Continue <ArrowRight size={14} />
        </button>
      </div>
    </div>
  );

  // ── Step 2: Exam cohort ──
  if (step === 2) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon"><Calendar size={32} /></div>
        <h2 className="ob-title">Which exam sitting are you preparing for?</h2>
        <p className="ob-text">
          This powers your countdown, priority ranking, and planner. You can update dates any time in Settings.
        </p>
        <div className="ob-cohort-grid">
          {COHORTS.map(c => (
            <button
              key={c.id}
              className={`ob-cohort-btn ${selectedCohort === c.id ? 'selected' : ''}`}
              onClick={() => {
                setSelectedCohort(c.id);
                if (c.id === 'manual') {
                  setShowManualDates(true);
                } else {
                  setShowManualDates(false);
                  if (c.flk1 !== null) setFlk1Date(c.flk1);
                  if (c.flk2 !== null) setFlk2Date(c.flk2);
                }
              }}>
              <span className="ob-cohort-label">{c.label}</span>
              <span className="ob-cohort-sub">{c.sub}</span>
            </button>
          ))}
        </div>
        {showManualDates && (
          <div className="ob-date-row" style={{ marginTop: 16 }}>
            <div className="ob-date-col">
              <label className="ob-label">FLK1 date</label>
              <input className="ob-input" type="date" value={flk1Date} onChange={e => setFlk1Date(e.target.value)} />
            </div>
            <div className="ob-date-col">
              <label className="ob-label">FLK2 date</label>
              <input className="ob-input" type="date" value={flk2Date} onChange={e => setFlk2Date(e.target.value)} />
            </div>
          </div>
        )}
        <div className="ob-btn-row" style={{ marginTop: 20 }}>
          <button className="ob-back" onClick={() => setStep(1)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => setStep(3)}>Continue <ArrowRight size={14} /></button>
        </div>
        <button className="ob-skip" onClick={() => setStep(3)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 3: Study materials ──
  if (step === 3) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon"><Books size={32} /></div>
        <h2 className="ob-title">Which study materials are you using?</h2>
        <p className="ob-text">
          This helps us give the most relevant guidance when importing MCQ results.
        </p>
        <div className="ob-provider-grid">
          {PROVIDERS.map(p => (
            <button key={p} className={`ob-provider-btn ${provider === p ? 'selected' : ''}`}
              onClick={() => setProvider(p)}>{p}</button>
          ))}
        </div>
        {provider === 'Other' && (
          <input className="ob-input" value={providerOther} onChange={e => setProviderOther(e.target.value)}
            placeholder="Which provider?" style={{ marginTop: 10 }} autoFocus />
        )}
        <div className="ob-btn-row">
          <button className="ob-back" onClick={() => setStep(2)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => setStep(4)}>Continue <ArrowRight size={14} /></button>
        </div>
        <button className="ob-skip" onClick={() => setStep(4)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 4: Targets ──
  if (step === 4) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <div className="ob-icon">🎯</div>
        <h2 className="ob-title">Set your study targets</h2>
        <p className="ob-text">
          These configure the planner and dashboard. You can change them any time in Settings.
        </p>

        <div style={{ width: '100%', marginBottom: 20 }}>
          <label className="ob-label">Daily MCQ target</label>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 8px' }}>How many practice questions do you want to complete each day?</p>
          <input className="ob-input" type="number" min="1" max="999" value={mcqTarget}
            onChange={e => setMcqTarget(e.target.value)} style={{ width: 100 }} />
        </div>

        <div style={{ width: '100%', marginBottom: 20 }}>
          <label className="ob-label">Preparation target</label>
          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 8px' }}>How confident do you want to be on each topic before the exam?</p>
          <div className="ob-provider-grid">
            {[
              { value: '0.60', label: 'Solid', desc: '60%' },
              { value: '0.70', label: 'Well prepared', desc: '70%' },
              { value: '0.80', label: 'Very prepared', desc: '80%' },
            ].map(opt => (
              <button key={opt.value}
                className={`ob-provider-btn ${prepTarget === opt.value ? 'selected' : ''}`}
                onClick={() => setPrepTarget(opt.value)}>
                {opt.label} ({opt.desc})
              </button>
            ))}
          </div>
        </div>

        <div className="ob-btn-row">
          <button className="ob-back" onClick={() => setStep(3)}><ArrowLeft size={14} /> Back</button>
          <button className="ob-btn" onClick={() => saveAndAdvance(false)} disabled={saving}>
            {saving ? 'Setting up' : <>Continue <ArrowRight size={14} /></>}
          </button>
        </div>
        <button className="ob-skip" onClick={() => saveAndAdvance(true)}>Skip for now</button>
      </div>
    </div>
  );

  // ── Step 5: Rate subject areas ──
  if (step === 5) {
    const ratedCount = Object.keys(subjectRatings).length;
    const flk1Subjects = subjectAreas.filter(s => s.flk === 'FLK1');
    const flk2Subjects = subjectAreas.filter(s => s.flk === 'FLK2');

    return (
      <div className="ob-fullpage">
        <div className="ob-fullpage-inner ob-fullpage-inner--wide">
          {dots}
          <h2 className="ob-title">How confident are you on each topic area?</h2>
          <p className="ob-text">
            Move the slider for each area. Rough guesses are fine — takes about 2 minutes.
          </p>

          {loadingSubjects ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--muted)' }}>Loading topic areas</div>
          ) : (
            <>
              {flk1Subjects.length > 0 && (
                <>
                  <div className="ob-rating-module-label">FLK1 — Functioning Legal Knowledge 1</div>
                  {flk1Subjects.map(s => (
                    <SubjectSlider
                      key={s.name}
                      subject={s}
                      value={subjectRatings[s.name] ?? null}
                      onChange={val => setSubjectRatings(prev => ({ ...prev, [s.name]: val }))}
                    />
                  ))}
                </>
              )}

              {flk2Subjects.length > 0 && (
                <>
                  <div className="ob-rating-module-label" style={{ marginTop: 24 }}>FLK2 — Functioning Legal Knowledge 2</div>
                  {flk2Subjects.map(s => (
                    <SubjectSlider
                      key={s.name}
                      subject={s}
                      value={subjectRatings[s.name] ?? null}
                      onChange={val => setSubjectRatings(prev => ({ ...prev, [s.name]: val }))}
                    />
                  ))}
                </>
              )}

              <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--hint)', margin: '16px 0 8px' }}>
                {ratedCount} of {subjectAreas.length} rated
              </div>

              <div className="ob-btn-row" style={{ marginTop: 12 }}>
                <button className="ob-back" onClick={() => setStep(4)}>
                  <ArrowLeft size={14} /> Back
                </button>
                <button
                  className="ob-btn"
                  disabled={ratedCount < subjectAreas.length || savingSubjects}
                  onClick={async () => {
                    setSavingSubjects(true);
                    try {
                      await Promise.all(
                        Object.entries(subjectRatings).map(([subjectName, confidence]) => {
                          const area = subjectAreas.find(s => s.name === subjectName);
                          return api('/api/topics/cascade-by-subject', {
                            method: 'POST',
                            body: JSON.stringify({
                              level: 'L1',
                              subject_area: subjectName,
                              module: area?.flk || 'FLK1',
                              confidence,
                            }),
                          });
                        })
                      );
                    } finally {
                      setSavingSubjects(false);
                    }
                    setStep(6);
                  }}
                >
                  {savingSubjects ? 'Saving' : <>Continue <ArrowRight size={14} /></>}
                </button>
              </div>
              <button className="ob-skip" onClick={() => { finishOnboarding(); }}>
                Skip for now
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Step 6: Go deeper (optional) ──
  if (step === 6) return (
    <div className="ob-fullpage">
      <div className="ob-fullpage-inner">
        {dots}
        <h2 className="ob-title">Ratings saved. Want to go deeper?</h2>
        <p className="ob-text">
          Your topic-area ratings have been applied to all topics. You can refine them any time.
        </p>
        <div className="ob-import-cards-v2">
          <button className="ob-import-card-v2" onClick={() => finishOnboarding('flk1')}>
            <div className="ob-import-card-v2-icon ob-import-card-v2-icon--blue">
              <Pencil size={20} />
            </div>
            <div className="ob-import-card-v2-body">
              <div className="ob-import-card-v2-title">Rate individual topics</div>
              <div className="ob-import-card-v2-text">Set confidence on each topic area. Takes 5-10 minutes.</div>
            </div>
            <ArrowRight size={16} className="ob-import-card-v2-arrow" />
          </button>

          <button className="ob-import-card-v2" onClick={() => finishOnboarding('import')}>
            <div className="ob-import-card-v2-icon ob-import-card-v2-icon--blue">
              <Upload size={20} />
            </div>
            <div className="ob-import-card-v2-body">
              <div className="ob-import-card-v2-title">Upload a spreadsheet</div>
              <div className="ob-import-card-v2-text">Import your own revision notes or use our template.</div>
            </div>
            <ArrowRight size={16} className="ob-import-card-v2-arrow" />
          </button>

          <button className="ob-import-card-v2 ob-import-card-v2--skip" onClick={() => finishOnboarding('dashboard')}>
            <div className="ob-import-card-v2-body">
              <div className="ob-import-card-v2-title">Skip for now</div>
              <div className="ob-import-card-v2-text">Go straight to your dashboard.</div>
            </div>
          </button>
        </div>

        <button className="ob-back ob-back--center" style={{ marginTop: 16 }} onClick={() => setStep(5)}>
          <ArrowLeft size={14} /> Back
        </button>
      </div>
    </div>
  );

  return null;
}

function SubjectSlider({ subject, value, onChange }) {
  const hasRating = value !== null && value !== undefined;
  const color = !hasRating ? 'var(--hint)'
    : value < 0.4 ? 'var(--red)'
    : value < 0.7 ? 'var(--amber)'
    : 'var(--green)';

  return (
    <div className="ob-subject-slider">
      <div className="ob-subject-slider-top">
        <span className="ob-subject-slider-name">{subject.name}</span>
        {subject.weighting && (
          <span className="ob-subject-weighting">{subject.weighting}</span>
        )}
        <span className="ob-subject-slider-pct" style={{ color }}>
          {hasRating ? Math.round(value * 100) + '%' : 'Not rated'}
        </span>
      </div>
      <input
        type="range" min="0" max="100" step="5"
        value={hasRating ? Math.round(value * 100) : 0}
        className="conf-slider"
        onChange={e => onChange(parseInt(e.target.value) / 100)}
        onMouseDown={() => { if (!hasRating) onChange(0.5); }}
      />
    </div>
  );
}
